// AddDesignChoiceModal — shown when a customer clicks "Add Design".
// Lets them pick between the guided Automated flow and jumping
// straight into the Manual builder (existing AddDesignModal tabs).
import { useEffect, useState } from 'react'
import type { CatProduct } from '../lib/catalog-types'

export default function AddDesignChoiceModal({
  open, onClose, onChooseAutomated, onChooseManual, garments,
}: {
  open: boolean
  onClose: () => void
  onChooseAutomated: () => void
  onChooseManual: (slug: string) => void
  garments: CatProduct[]
}) {
  const [view, setView] = useState<'choice' | 'garment'>('choice')

  useEffect(() => { if (open) setView('choice') }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-cream rounded-lg shadow-2xl max-w-2xl w-full overflow-hidden border border-brick/40">

        {/* Header */}
        <div className="bg-brick text-white px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {view === 'garment' && (
              <button onClick={() => setView('choice')}
                className="w-7 h-7 rounded hover:bg-white/15 flex items-center justify-center shrink-0" aria-label="Back">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                  <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                </svg>
              </button>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">
                {view === 'choice' ? 'Add Design' : 'Manual'}
              </div>
              <div className="text-sm font-heading font-bold uppercase tracking-wide">
                {view === 'choice' ? 'How would you like to design?' : 'Which garment are you designing for?'}
              </div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded hover:bg-white/15 flex items-center justify-center" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Garment picker — shown after clicking Manual */}
        {view === 'garment' && (
          <div className="p-5">
            <p className="text-sm text-ink-2 mb-4">Select a garment to open in the editor.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {garments.map((g) => (
                <button key={g.id} onClick={() => onChooseManual(g.slug)}
                  className="text-left px-4 py-3.5 rounded-lg border-2 border-border bg-white hover:border-ink hover:bg-surface transition-colors group">
                  <div className="font-heading font-bold text-base text-ink uppercase">{g.name}</div>
                  {g.colours.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {g.colours.slice(0, 8).map((c) => (
                        <span key={c.id} title={c.name}
                          className="w-4 h-4 rounded-full border border-black/10 shrink-0"
                          style={{ background: c.hex }} />
                      ))}
                      {g.colours.length > 8 && (
                        <span className="text-[10px] text-ink-3 self-center">+{g.colours.length - 8}</span>
                      )}
                    </div>
                  )}
                  <div className="text-[11px] font-bold uppercase tracking-wide text-ink-3 group-hover:text-ink mt-2 flex items-center gap-1">
                    Select
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Option cards */}
        {view === 'choice' && <div className="p-5 grid md:grid-cols-2 gap-4">

          {/* Automated */}
          <button onClick={onChooseAutomated}
            className="text-left rounded-lg border-2 border-brick/30 bg-white p-5 hover:border-brick hover:bg-brick-tint/30 transition-all group">
            <div className="w-10 h-10 rounded-full bg-brick text-white flex items-center justify-center mb-3 shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <div className="font-heading font-bold text-xl text-ink uppercase mb-1">Automated</div>
            <div className="text-xs text-ink-3 mb-4 leading-relaxed">We guide you through everything — perfect if you're not sure where to start.</div>
            <ul className="space-y-1.5 text-xs text-ink-2 mb-4">
              {[
                'Pick your garment colour',
                'Generate a logo with AI or upload your own',
                'Choose front only or front + back',
                'Set print placement',
                'Select sizes & quantities',
                'Review and confirm',
              ].map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-brick/10 text-brick flex items-center justify-center shrink-0">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  {s}
                </li>
              ))}
            </ul>
            <div className="text-[11px] font-bold uppercase tracking-wide text-brick group-hover:underline flex items-center gap-1">
              Start guided flow
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
          </button>

          {/* Manual */}
          <button onClick={() => setView('garment')}
            className="text-left rounded-lg border-2 border-border bg-white p-5 hover:border-ink hover:bg-surface transition-all group">
            <div className="w-10 h-10 rounded-full bg-ink text-white flex items-center justify-center mb-3 shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </div>
            <div className="font-heading font-bold text-xl text-ink uppercase mb-1">Manual</div>
            <div className="text-xs text-ink-3 mb-4 leading-relaxed">Jump straight into the designer. Full control over every detail.</div>
            <ul className="space-y-1.5 text-xs text-ink-2 mb-4">
              {[
                'Upload artwork or generate with AI',
                'Full canvas drag-and-drop control',
                'Multi-location printing (front, back, sleeves)',
                'Precise size & DPI readout',
              ].map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-ink/10 text-ink-3 flex items-center justify-center shrink-0">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  {s}
                </li>
              ))}
            </ul>
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-3 group-hover:text-ink group-hover:underline flex items-center gap-1">
              Open builder
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
          </button>
        </div>}
      </div>
    </div>
  )
}
